/**
 * KhiMonitoringCard — график за проследяване по стадий.
 * Данни: GET /api/khi/monitoring-schedule
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, AlertCircle, CheckCircle2, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ST = {
  overdue: { color: "#EF4444", label: "ПРОСРОЧЕНО", Icon: AlertCircle },
  soon: { color: "#F59E0B", label: "СКОРО", Icon: CalendarClock },
  ok: { color: "#10B981", label: "НАРЕД", Icon: CheckCircle2 },
  unknown: { color: "#94A3B8", label: "НЯМА ДАТА", Icon: HelpCircle },
};

const every = (m) => (m === 1 ? "всеки месец" : m === 12 ? "веднъж годишно" : `на ${m} месеца`);

export default function KhiMonitoringCard({ cardCls }) {
  const [data, setData] = useState(null);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/khi/monitoring-schedule`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  const remind = async () => {
    setReminding(true);
    try {
      const { data: res } = await axios.post(`${API}/khi/monitoring-check`);
      toast.success(res.count
        ? `Изпратено напомняне за ${res.count} изследване`
        : "Вече Ви напомнихме през последните 14 дни");
    } catch {
      toast.error("Напомнянето не успя");
    } finally {
      setReminding(false);
    }
  };

  if (!data?.plan?.length) return null;

  return (
    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="khi-monitoring-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-cyan-500" />
            График за проследяване
          </h3>
          <p className="text-[11px] opacity-60 mt-0.5">
            Стадий {data.stage || "—"}
            {data.egfr != null ? ` · филтрация ${data.egfr} mL/min` : ""}
          </p>
        </div>
        {data.overdue_count > 0 && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-500"
            data-testid="khi-monitoring-overdue-count">
            {data.overdue_count} просрочени
          </span>
        )}
      </div>

      {data.overdue_count > 0 && (
        <button onClick={remind} disabled={reminding}
          className="mt-2 w-full text-[11px] font-bold px-3 py-2 rounded-xl
                     bg-cyan-500 text-white hover:bg-cyan-600 active:scale-[0.98]
                     transition-colors disabled:opacity-60"
          data-testid="khi-monitoring-remind-btn">
          {reminding ? "Изпращам…" : "Напомни ми за просрочените"}
        </button>
      )}

      <div className="mt-3 space-y-2">
        {data.plan.map((p) => {
          const s = ST[p.status] || ST.unknown;
          return (
            <div key={p.code} className="rounded-xl p-2.5"
              style={{ background: s.color + "10", borderLeft: `3px solid ${s.color}` }}
              data-testid={`khi-monitoring-item-${p.code}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[12px] font-bold">{p.title_bg}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  style={{ background: s.color + "22", color: s.color }}>
                  <s.Icon className="w-3 h-3" /> {s.label}
                </span>
              </div>
              <div className="text-[10px] opacity-70 mt-0.5">
                {every(p.every_months)}
                {p.last_date ? ` · последно ${p.last_date}` : " · няма записано изследване"}
                {p.due_date ? ` · следващо до ${p.due_date}` : ""}
              </div>
              <div className="text-[10px] opacity-50 mt-0.5">{p.why_bg}</div>
            </div>
          );
        })}
      </div>

      <div className="text-[9px] opacity-50 mt-2">{data.disclaimer_bg}</div>
    </div>
  );
}
